/**
 * Cloud Kitchen Model Configuration
 * COMPLETE extraction from /server/cloudkitchen/cloudkitchen-assumptions.js
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const cloudkitchenConfig: ModelConfig = {
  modelType: 'cloudkitchen',
  apiEndpoint: '/api/cloudkitchen',
  displayName: 'Cloud Kitchen',
  resultTabs: standardResultTabs,
  description: 'Delivery-only kitchen model with platform integration and order optimization.',
  icon: '🍳',
  color: '#f59e0b',

  assumptionGroups: [
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        { id: 'userEmail', label: 'Your Email', type: 'email', placeholder: 'name@company.com' },
      ],
    },
    {
      id: 'kitchen-ops',
      title: '🍳 Kitchen Operations',
      fields: [
        { id: 'kitchenCapacity', label: 'Max Orders/Day', type: 'number', defaultValue: 180 },
        { id: 'ordersPerDay', label: 'Current Orders/Day', type: 'number', defaultValue: 125 },
        { id: 'avgOrderValue', label: 'Avg Order Value ($)', type: 'number', defaultValue: 22 },
        { id: 'operatingDaysMonth', label: 'Operating Days/Month', type: 'number', defaultValue: 28 },
      ],
    },
    {
      id: 'growth-efficiency',
      title: '📈 Growth & Efficiency',
      fields: [
        { id: 'capacityUtilizationGrowth', label: 'Capacity Utilization Growth (%)', type: 'number', defaultValue: 20 },
        { id: 'menuOptimizationRate', label: 'Menu Optimization (%)', type: 'number', defaultValue: 12 },
        { id: 'avgOrderValueGrowth', label: 'AOV Growth (%)', type: 'number', defaultValue: 8 },
        { id: 'annualOrderGrowthRate', label: 'Annual Order Growth (%)', type: 'number', defaultValue: 35 },
      ],
    },
    {
      id: 'utilization-by-year',
      title: '📊 Capacity Utilization by Year',
      fields: [
        { id: 'capacityUtilizationY1', label: 'Y1 Utilization (%)', type: 'number', defaultValue: 70 },
        { id: 'capacityUtilizationY2', label: 'Y2 Utilization (%)', type: 'number', defaultValue: 75 },
        { id: 'capacityUtilizationY3', label: 'Y3 Utilization (%)', type: 'number', defaultValue: 80 },
        { id: 'capacityUtilizationY4', label: 'Y4 Utilization (%)', type: 'number', defaultValue: 85 },
        { id: 'capacityUtilizationY5', label: 'Y5 Utilization (%)', type: 'number', defaultValue: 85 },
      ],
    },
    {
      id: 'operations-cogs',
      title: '⚙️ Operations & COGS',
      fields: [
        { id: 'foodCostPct', label: 'Food Cost (%)', type: 'number', defaultValue: 35 },
        { id: 'kitchenLaborPct', label: 'Kitchen Labor (%)', type: 'number', defaultValue: 18 },
        { id: 'kitchenRentMonth', label: 'Monthly Kitchen Rent ($)', type: 'number', defaultValue: 8500 },
        { id: 'packagingCostPct', label: 'Packaging Cost (%)', type: 'number', defaultValue: 4 },
        { id: 'utilitiesPct', label: 'Utilities (%)', type: 'number', defaultValue: 5 },
      ],
    },
    {
      id: 'platform-integration',
      title: '📱 Platform Integration',
      fields: [
        { id: 'deliveryPlatformCommission', label: 'Platform Commission (%)', type: 'number', defaultValue: 18 },
        { id: 'directOrdersPct', label: 'Direct Orders (%)', type: 'number', defaultValue: 35 },
      ],
    },
    {
      id: 'marketing-acquisition',
      title: '📢 Marketing & Acquisition',
      fields: [
        { id: 'salesMarketingPct', label: 'Marketing Spend (%)', type: 'number', defaultValue: 12 },
        { id: 'customerAcquisitionCost', label: 'Customer Acquisition Cost ($)', type: 'number', defaultValue: 25 },
      ],
    },
    {
      id: 'fixed-costs',
      title: '🏢 Fixed Costs',
      fields: [
        { id: 'monthlyInsurance', label: 'Monthly Insurance ($)', type: 'number', defaultValue: 800 },
        { id: 'monthlyTechCosts', label: 'Monthly Tech Costs ($)', type: 'number', defaultValue: 600 },
        { id: 'monthlyLicenses', label: 'Monthly Licenses ($)', type: 'number', defaultValue: 200 },
        { id: 'maintenancePct', label: 'Maintenance (%)', type: 'number', defaultValue: 3 },
        { id: 'gaPct', label: 'G&A (%)', type: 'number', defaultValue: 8 },
      ],
    },
    {
      id: 'initial-investments',
      title: '💰 Initial Investments',
      fields: [
        { id: 'initialEquipmentValue', label: 'Kitchen Equipment ($)', type: 'number', defaultValue: 85000 },
        { id: 'initialDepositsCosts', label: 'Deposits & Setup ($)', type: 'number', defaultValue: 15000 },
        { id: 'minimumCashBalance', label: 'Minimum Cash ($)', type: 'number', defaultValue: 25000 },
      ],
    },
    {
      id: 'working-capital',
      title: '💵 Working Capital',
      fields: [
        { id: 'daysInAccountsReceivable', label: 'Days in AR', type: 'number', defaultValue: 3 },
        { id: 'daysInInventory', label: 'Inventory Days', type: 'number', defaultValue: 5 },
        { id: 'daysInAccountsPayable', label: 'Days in AP', type: 'number', defaultValue: 20 },
        { id: 'prepaidExpensesPct', label: 'Prepaid Expenses (%)', type: 'number', defaultValue: 3 },
        { id: 'accruedExpensesPct', label: 'Accrued Expenses (%)', type: 'number', defaultValue: 6 },
      ],
    },
    {
      id: 'depreciation',
      title: '📉 Depreciation',
      fields: [
        { id: 'equipmentDepreciationRate', label: 'Equipment Depreciation (%)', type: 'number', defaultValue: 25 },
      ],
    },
    {
      id: 'financing',
      title: '🏦 Financing',
      fields: [
        { id: 'initialCapitalRaise', label: 'Initial Capital ($)', type: 'number', defaultValue: 150000 },
        { id: 'year2CapitalRaise', label: 'Year 2 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year3CapitalRaise', label: 'Year 3 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year4CapitalRaise', label: 'Year 4 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year5CapitalRaise', label: 'Year 5 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'initialDebtAmount', label: 'Initial Debt ($)', type: 'number', defaultValue: 60000 },
        { id: 'debtRepaymentYears', label: 'Repayment Years', type: 'number', defaultValue: 5 },
        { id: 'annualInterestRate', label: 'Interest Rate (%)', type: 'number', defaultValue: 8 },
        { id: 'automaticCapitalRaise', label: 'Automatic Capital Raise', type: 'checkbox', defaultValue: true, helpText: 'Automatically raise capital when cash goes negative' },
      ],
    },
    {
      id: 'tax',
      title: '🏛️ Tax',
      fields: [
        { id: 'corporateTaxRate', label: 'Corporate Tax Rate (%)', type: 'number', defaultValue: 25 },
      ],
    },
    {
      id: 'capex',
      title: '📊 CapEx',
      fields: [
        { id: 'maintenanceCapexPct', label: 'Maintenance CapEx (%)', type: 'number', defaultValue: 2 },
      ],
    },
    {
      id: 'annual-increases',
      title: '📈 Annual Increases',
      fields: [
        { id: 'annualRentIncrease', label: 'Rent Increase (%)', type: 'number', defaultValue: 4 },
        { id: 'annualWageIncrease', label: 'Wage Increase (%)', type: 'number', defaultValue: 5 },
      ],
    },
    {
      id: 'quality-metrics',
      title: '⭐ Quality Metrics',
      fields: [
        { id: 'orderFulfillmentTime', label: 'Order Fulfillment Time (min)', type: 'number', defaultValue: 25 },
        { id: 'customerSatisfactionTarget', label: 'Customer Satisfaction Target (%)', type: 'number', defaultValue: 85 },
        { id: 'menuItemCount', label: 'Menu Item Count', type: 'number', defaultValue: 45 },
      ],
    },
  ],

};

export const cloudkitchenDefaultAssumptions = {
  kitchenCapacity: 180,
  ordersPerDay: 125,
  avgOrderValue: 22,
  operatingDaysMonth: 28,
  capacityUtilizationGrowth: 20,
  menuOptimizationRate: 12,
  avgOrderValueGrowth: 8,
  capacityUtilizationY1: 70,
  capacityUtilizationY2: 75,
  capacityUtilizationY3: 80,
  capacityUtilizationY4: 85,
  capacityUtilizationY5: 85,
  foodCostPct: 35,
  kitchenLaborPct: 18,
  kitchenRentMonth: 8500,
  packagingCostPct: 4,
  utilitiesPct: 5,
  deliveryPlatformCommission: 18,
  directOrdersPct: 35,
  salesMarketingPct: 12,
  customerAcquisitionCost: 25,
  monthlyInsurance: 800,
  monthlyTechCosts: 600,
  monthlyLicenses: 200,
  maintenancePct: 3,
  gaPct: 8,
  initialEquipmentValue: 85000,
  initialDepositsCosts: 15000,
  minimumCashBalance: 25000,
  daysInAccountsReceivable: 3,
  daysInInventory: 5,
  daysInAccountsPayable: 20,
  prepaidExpensesPct: 3,
  accruedExpensesPct: 6,
  equipmentDepreciationRate: 25,
  initialCapitalRaise: 150000,
  year2CapitalRaise: 0,
  year3CapitalRaise: 0,
  year4CapitalRaise: 0,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
  initialDebtAmount: 60000,
  debtRepaymentYears: 5,
  annualInterestRate: 8,
  corporateTaxRate: 25,
  maintenanceCapexPct: 2,
  annualOrderGrowthRate: 35,
  annualRentIncrease: 4,
  annualWageIncrease: 5,
  orderFulfillmentTime: 25,
  customerSatisfactionTarget: 85,
  menuItemCount: 45,
};
